import React from "react";
import Markdown from "markdown-to-jsx";

const elements = [
	"a",
	"abbr",
	"address",
	"area",
	"article",
	"aside",
	"audio",
	"b",
	"base",
	"bdi",
	"bdo",
	"big",
	"blockquote",
	"body",
	"br",
	"button",
	"canvas",
	"caption",
	"cite",
	"code",
	"col",
	"colgroup",
	"data",
	"datalist",
	"dd",
	"del",
	"details",
	"dfn",
	"dialog",
	"div",
	"dl",
	"dt",
	"em",
	"embed",
	"fieldset",
	"figcaption",
	"figure",
	"footer",
	"form",
	"h1",
	"h2",
	"h3",
	"h4",
	"h5",
	"h6",
	"head",
	"header",
	"hgroup",
	"hr",
	"html",
	"i",
	"iframe",
	"img",
	"input",
	"ins",
	"kbd",
	"keygen",
	"label",
	"legend",
	"li",
	"link",
	"main",
	"map",
	"mark",
	"marquee",
	"menu",
	"menuitem",
	"meta",
	"meter",
	"nav",
	"noscript",
	"object",
	"ol",
	"optgroup",
	"option",
	"output",
	"p",
	"param",
	"picture",
	"pre",
	"progress",
	"q",
	"rp",
	"rt",
	"ruby",
	"s",
	"samp",
	"script",
	"section",
	"select",
	"small",
	"source",
	"span",
	"strong",
	"style",
	"sub",
	"summary",
	"sup",
	"table",
	"tbody",
	"td",
	"textarea",
	"tfoot",
	"th",
	"thead",
	"time",
	"title",
	"tr",
	"track",
	"u",
	"ul",
	"var",
	"video",
	"wbr",

	// SVG
	"circle",
	"clipPath",
	"defs",
	"ellipse",
	"foreignObject",
	"g",
	"image",
	"line",
	"linearGradient",
	"marker",
	"mask",
	"path",
	"pattern",
	"polygon",
	"polyline",
	"radialGradient",
	"rect",
	"stop",
	"svg",
	"text",
	"tspan",
];

const allowedElements = ["a", "b", "strong", "em", "u", "code", "del"];

const CustomLink = ({ children, ...props }) => (
	<a {...props} target="_blank" rel="noopener noreferrer">
		{children}
	</a>
);

const Blue = ({ children }) => (
	<span style={{ color: "#1f6feb" }}>{children}</span>
);

const MarkdownWrapper = ({ children }) => {
    // const isProbablyUrl = str => /^https?:\/\/\S+$/.test(str);
    // const isProbablyUrl = str => /https?:\/\/\S+/.test(str);

    // --- sanitize inicial ---
    let text = children || '';
    if (text && text.includes('BEGIN:VCARD')) text = '';
    if (text && text.includes('data:image/')) text = '';

    // --- formatação *...* , ~...~ e _..._ com proteção ---
    if (text) {
        const OPEN = '\uFFF0';
        const CLOSE = '\uFFF1';
		if ((text.toLocaleLowerCase().includes("<b>") && text.toLocaleLowerCase().includes("</b>")) || (text.toLocaleLowerCase().includes("<strong>") && text.toLocaleLowerCase().includes("</strong>"))) {
			text = text.replace(/<b>([\s\S]+?)<\/b>/g, `${OPEN}$1${CLOSE}`);
			text = text.replace(/<B>([\s\S]+?)<\/B>/g, `${OPEN}$1${CLOSE}`);
			text = text.replace(/<strong>([\s\S]+?)<\/strong>/g, `${OPEN}$1${CLOSE}`);
			text = text.replace(/<STRONG>([\s\S]+?)<\/STRONG>/g, `${OPEN}$1${CLOSE}`);
			text = text.replaceAll(OPEN, '*').replaceAll(CLOSE, '*');
		}

        // 0) PROTEGER URLs (placeholder sem *_~)
        const urlRegex = /https?:\/\/[^\s<>()]+/g;
        const urls = [];
        text = text.replace(urlRegex, m => {
            urls.push(m);
            return `URLPH${urls.length - 1}`;
        });

        // 1) *texto* (ignora ** e não casa no meio da palavra)
        const singleStarPair = /(?<![\p{L}\p{N}])\*(?!\*)([^*\n]+?)(?<!\*)\*(?![\p{L}\p{N}])/gu;
        text = text.replace(singleStarPair, `${OPEN}$1${CLOSE}`);
        text = text.replace(/\*/g, '\\*');
        text = text.replaceAll(OPEN, '**').replaceAll(CLOSE, '**');

        // 2) ~texto~
        const tildePair = /(?<![\p{L}\p{N}])~(?!~)([^~\n]+?)(?<!~)~(?![\p{L}\p{N}])/gu;
        text = text
            .replace(tildePair, `${OPEN}$1${CLOSE}`)
            .replace(/~/g, '\\~')
            .replaceAll(OPEN, '~~')
            .replaceAll(CLOSE, '~~');

        // 3) _texto_ (ignora __ e não casa no meio da palavra)
        const singleUnderscorePair = /(?<![\p{L}\p{N}])_(?!_)([^_\n]+?)(?<!_)_(?![\p{L}\p{N}])/gu;
        text = text.replace(singleUnderscorePair, `${OPEN}$1${CLOSE}`);
        text = text.replace(/_/g, '\\_');
        text = text.replaceAll(OPEN, '_').replaceAll(CLOSE, '_');

        // 4) RESTAURAR URLs (depois de todas as escapadas)
        text = text.replace(/URLPH(\d+)/g, (_, i) => urls[Number(i)]);
    }


    // [[blue]]...[[/blue]]
    text = text?.replace(/\[\[blue\]\]([\s\S]+?)\[\[\/blue\]\]/g, "<Blue>$1</Blue>");
    text = text?.replace(/<\/?([A-Za-z][\w:-]*)\b[^>]*>/g, (m, tag) => {
        return tag === "Blue" ? m : m.replace(/</g, "&lt;").replace(/>/g, "&gt;");
    });

    const options = React.useMemo(() => {
        const markdownOptions = {
            disableParsingRawHTML: false,
            forceInline: true,
            overrides: {
                a: { component: CustomLink },
                Blue: { component: Blue },
            },
        };

        elements.forEach(element => {
            if (!allowedElements.includes(element)) {
                markdownOptions.overrides[element] = el => el.children || null;
            }
        });

        return markdownOptions;
    }, []);

    // retorno condicional SÓ depois do hook ter sido chamado
    if (!text) return null;

    return <Markdown options={options}>{text}</Markdown>;
};


export default MarkdownWrapper;